import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { IItemForGetShortInfo, IGetRestaurantsShort, IGetRestaurantsShortArgs, EntityStatus } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { SHOW_ARCHIVED_RESTAURANTS } from 'appConstants';
import { ISelectOption } from 'components/Select/types';
import FormSelect from 'components/Select/FormSelect';
import { DropdownRender } from 'components/Select/DropdownRender';
import { ShowArchived } from 'components/Select/DropdownRender/ShowArchived';
import { ArchivedLabel } from 'components/Select/ArchivedLabel';
import { TEditDeviceSelectedItem } from 'modules/Devices/EditDevice/editDeviceState/editDeviceContext';
import { getRestaurantsShortInfo } from 'backend/restaurants';

interface IRestaurantField {
	setFetchedRestaurants: (value: IItemForGetShortInfo[]) => void;
	setIDRestaurant: (value: TEditDeviceSelectedItem) => void;
	IDSelectedCustomer: TEditDeviceSelectedItem;
	IDSelectedRestaurant: TEditDeviceSelectedItem;
}

export const RestaurantField: React.FC<IRestaurantField> = React.memo(
	({ setFetchedRestaurants, setIDRestaurant, IDSelectedCustomer, IDSelectedRestaurant }) => {
		const { response, loading, refetch, responseError } = useFetchData<
			IGetRestaurantsShort,
			IGetRestaurantsShortArgs
		>(IDSelectedCustomer ? getRestaurantsShortInfo : null, {
			customerId: IDSelectedCustomer || '',
		});
		const [showArchived, setShowArchived] = useState<boolean>(
			localStorage.getItem(SHOW_ARCHIVED_RESTAURANTS) === 'true',
		);

		useEffect(() => {
			refetch();
		}, [IDSelectedCustomer]);

		useEffect(() => {
			if (response) {
				setFetchedRestaurants(response.data.restaurants);
			}
		}, [response]);

		const options = useMemo<ISelectOption[]>(() => {
			const items = response?.data.restaurants || [];
			const visibleItems = showArchived
				? items
				: items.filter(
						(restaurant) =>
							restaurant.status === EntityStatus.Active || restaurant.id === IDSelectedRestaurant,
				  );

			return visibleItems.map((restaurant) => ({
				...restaurant,
				additionalContent: restaurant.status === EntityStatus.Archived ? <ArchivedLabel /> : undefined,
			}));
		}, [response, showArchived, IDSelectedRestaurant]);

		const toggleShowArchived = useCallback(() => {
			localStorage.setItem(SHOW_ARCHIVED_RESTAURANTS, JSON.stringify(!showArchived));
			setShowArchived((prevValue) => !prevValue);
		}, [showArchived]);

		const renderSelectMenu = useCallback(
			(menu: React.ReactElement) => (
				<DropdownRender menu={menu}>
					<ShowArchived checked={showArchived} onChange={toggleShowArchived} />
				</DropdownRender>
			),
			[showArchived, toggleShowArchived],
		);

		return (
			<FormSelect
				testId="RestaurantField"
				name="IDSelectedRestaurant"
				label="restaurantLabel"
				options={options}
				fetchError={responseError}
				loading={loading}
				onRefetch={refetch}
				disabled={!IDSelectedCustomer || loading}
				onChange={setIDRestaurant}
				keepSearchWithDropdownRender={true}
				dropdownRender={renderSelectMenu}
			/>
		);
	},
);
